import { useMemo, useState } from "react";
import { Calculator, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/format";

const inputCls =
  "h-11 w-full rounded-sm border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary";

export const FinancingCalculator = ({ defaultPrice = 75000 }) => {
  const [price, setPrice] = useState(defaultPrice);
  const [down, setDown] = useState(Math.round(defaultPrice * 0.1));
  const [rate, setRate] = useState(7.9);
  const [term, setTerm] = useState(60);

  const { monthly, financed, totalInterest } = useMemo(() => {
    const principal = Math.max(0, Number(price) - Number(down));
    const r = Number(rate) / 100 / 12;
    const n = Number(term);
    const m = r > 0 ? (principal * r) / (1 - Math.pow(1 + r, -n)) : principal / n;
    return {
      monthly: isFinite(m) ? m : 0,
      financed: principal,
      totalInterest: isFinite(m) ? m * n - principal : 0,
    };
  }, [price, down, rate, term]);

  const reset = () => {
    setPrice(defaultPrice);
    setDown(Math.round(defaultPrice * 0.1));
    setRate(7.9);
    setTerm(60);
    toast.success("Calculator reset to defaults");
  };

  return (
    <div className="bg-card border border-border rounded-md p-6 sm:p-8" data-testid="financing-calculator">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 border border-primary/25 p-2 rounded-sm">
            <Calculator className="h-5 w-5 text-primary" />
          </div>
          <h3 className="font-display text-xl font-bold uppercase text-foreground">Payment Calculator</h3>
        </div>
        <button
          onClick={reset}
          className="text-muted-foreground hover:text-primary transition-colors"
          aria-label="Reset calculator"
          data-testid="fc-reset-button"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-6">
        <div>
          <label htmlFor="fc-price" className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-2">Vehicle Price</label>
          <input
            id="fc-price"
            type="number"
            min={1000}
            max={500000}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className={inputCls}
            data-testid="fc-price-input"
          />
          <input
            type="range"
            min={1000}
            max={250000}
            step={500}
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="w-full mt-2 accent-[#EA580C]"
            aria-label="Vehicle price slider"
            data-testid="fc-price-slider"
          />
        </div>
        <div>
          <label htmlFor="fc-down" className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-2">Down Payment / Trade-In</label>
          <input
            id="fc-down"
            type="number"
            min={0}
            max={250000}
            value={down}
            onChange={(e) => setDown(e.target.value)}
            className={inputCls}
            data-testid="fc-down-input"
          />
          <input
            type="range"
            min={0}
            max={100000}
            step={250}
            value={down}
            onChange={(e) => setDown(e.target.value)}
            className="w-full mt-2 accent-[#EA580C]"
            aria-label="Down payment slider"
            data-testid="fc-down-slider"
          />
        </div>
        <div>
          <label htmlFor="fc-rate" className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-2">
            Interest Rate — <span className="text-primary">{rate}%</span>
          </label>
          <input
            id="fc-rate"
            type="range"
            min={0}
            max={20}
            step={0.1}
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            className="w-full mt-3 accent-[#EA580C]"
            data-testid="fc-rate-slider"
          />
        </div>
        <div>
          <label htmlFor="fc-term" className="text-xs font-mono uppercase tracking-wider text-muted-foreground block mb-2">Loan Term</label>
          <select id="fc-term" value={term} onChange={(e) => setTerm(e.target.value)} className={inputCls} data-testid="fc-term-select">
            {[24, 36, 48, 60, 72, 84].map((t) => (
              <option key={t} value={t}>{t} months</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-muted border border-primary/30 rounded-sm p-6 text-center mb-4">
        <p className="text-xs font-mono uppercase tracking-[0.25em] text-muted-foreground mb-1">Estimated Monthly Payment</p>
        <p className="font-display text-4xl sm:text-5xl font-black text-primary" data-testid="fc-monthly-payment">
          {fmtMoney(monthly)}<span className="text-base text-muted-foreground font-normal">/mo</span>
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 text-center">
        <div className="bg-muted border border-border rounded-sm py-3 px-2">
          <p className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Amount Financed</p>
          <p className="text-sm font-bold text-foreground" data-testid="fc-amount-financed">{fmtMoney(financed)}</p>
        </div>
        <div className="bg-muted border border-border rounded-sm py-3 px-2">
          <p className="text-[10px] font-mono uppercase text-muted-foreground mb-1">Total Interest</p>
          <p className="text-sm font-bold text-foreground" data-testid="fc-total-interest">{fmtMoney(totalInterest)}</p>
        </div>
      </div>
      <p className="text-[11px] text-muted-foreground mt-4">
        *Estimate only, in Canadian dollars. Final terms are subject to credit approval and may differ. Taxes and fees are not included.
      </p>
    </div>
  );
};
